<template>
  <div class="feedback-title">
    <h2 class="feedback-description">Feedback Page</h2>
    <p>
      Welcome to the feedback page for Discovery, the NTT Data web application designed to facilitate the management
      and discovery of startups. Here you can share your opinions on the user experience and site features.
      Discovery allows startups to register, describe their services, participate in challenges, and get discovered
      through a chatbot interface. Users can filter and efficiently find relevant startups by clicking on different
      chatbot options to narrow down the search based on the criteria provided during the startup's creation.
      Additionally, Discovery offers extra features such as a challenges module, a news module, and onboarding slides
      for startups on the homepage. Tell us what you think about the site's navigability, intuitiveness, and how the
      chatbot helped you find what you were looking for.
    </p>
    <div class="feedback-container">
      <div class="feedback-box">
        <h2 v-if="!isEditing">Write Your Review</h2>
        <h2 v-else>Edit Your Review</h2>
        <div class="feedback-form">
          <input v-model="feedback" type="text" placeholder="Write your review here..." aria-label="Review" />
          <div class="star-rating">
            <span v-for="star in 5" :key="star" class="star" :class="{ active: rating >= star }" @click="rate(star)"
              aria-label="Rating Star">
              &#9733;
            </span>
          </div>
          <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
          <button @click="submittedFeedback ? updateFeedback() : sendFeedback()">
            {{ submittedFeedback ? 'UPDATE' : 'SEND' }}
          </button>
        </div>
        <div v-if="submittedFeedback" class="submitted-feedback">
          <h3>Your Submitted Review</h3>
          <p>{{ submittedFeedback.feedback }}</p>
          <div class="star-rating">
            <span v-for="star in 5" :key="star" class="star" :class="{ active: submittedFeedback.rating >= star }">
              &#9733;
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UrlsService from "@/helpers/UrlsService";
import TimeService from "@/helpers/TimeService";
import APILoaderDecoratorService from "@/helpers/APILoaderDecoratorService";

export default {
  data() {
    return {
      feedback: '',
      rating: 0,
      isEditing: false,
      submittedFeedback: null,
      errorMessage: ''
    };
  },
  computed: {
    userInfo() {
      return this.$store.getters['UserState/getUserInfo']; // Access user information from Vuex
    }
  },
  methods: {
    rate(star) {
      this.rating = star;
    },
    async sendFeedback() {
      if (this.rating === 0) {
        this.errorMessage = 'Please rate at least one star before submitting your feedback.';
        return;
      }

      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const formattedDate = `${day}-${month}-${year}`;

      let requestUrl = {
        url: UrlsService.uploadReviews,
        timeout: TimeService.secondsToMillis(0),
      };
      let requestData = {
        Name: this.userInfo.username, // Use dynamic username from Vuex
        Role: this.userInfo.role,     // Use dynamic role from Vuex
        Reviews: {
          Date: formattedDate,
          Text: this.feedback,
          Value: this.rating.toString()
        }
      };
      let requestObject = {
        type: "public",
        urlsInfo: requestUrl,
        data: {
          data: requestData,
        },
      };
      let messages = {
        title: "Uploading Reviews",
        error: "Error uploading Reviews",
      };
      let options = {
        loadingType: "notification",
        timeout: TimeService.secondsToMillis(0),
      };
      try {
        await APILoaderDecoratorService.setLoader(requestObject, messages, options);
        this.submittedFeedback = { feedback: this.feedback, rating: this.rating }; // Update submitted feedback
        this.errorMessage = ''; // Clear error message
        this.feedback = ''; // Reset feedback
        this.rating = 0; // Reset rating
      } catch (error) {
        this.errorMessage = 'Failed to send feedback. Please try again.';
      }
    },
    async updateFeedback() {
      if (this.rating === 0) {
        this.errorMessage = 'Please rate at least one star before updating your feedback.';
        return;
      }
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const formattedDate = `${day}-${month}-${year}`;

      let requestUrl = {
        url: UrlsService.uploadReviews, // Assuming the same endpoint for updating reviews
        timeout: TimeService.secondsToMillis(0),
      };
      let requestData = {
        Name: this.userInfo.username, // Use dynamic username from Vuex
        Role: this.userInfo.role,     // Use dynamic role from Vuex
        Reviews: {
          Date: formattedDate,
          Text: this.feedback,
          Value: this.rating.toString()
        }
      };
      let requestObject = {
        type: "public",
        urlsInfo: requestUrl,
        data: {
          data: requestData,
        },
      };
      let messages = {
        title: "Updating Reviews",
        error: "Error updating Reviews",
      };
      let options = {
        loadingType: "notification",
        timeout: TimeService.secondsToMillis(0),
      };
      try {
        await APILoaderDecoratorService.setLoader(requestObject, messages, options);
        this.submittedFeedback = { feedback: this.feedback, rating: this.rating }; // Update submitted feedback
        this.errorMessage = ''; // Clear error message
        this.isEditing = false;
        this.feedback = ''; // Reset feedback
        this.rating = 0; // Reset rating
      } catch (error) {
        this.errorMessage = 'Failed to update feedback. Please try again.';
      }
    },
    async fetchLastReview() {
      const requestUrl = UrlsService.getAllReviews;
      try {
        const response = await axios.get(requestUrl);
        if (response.ok) {
          const review = await response.json();
          if (review && review.length > 0) {
            return {
              feedback: review[0].Text,
              rating: parseInt(review[0].Value, 10)
            };
          }
        } else if (response.status === 204) {
          console.log('No review found for this user.');
          return null;
        } else {
          console.error('Failed to fetch last review:', response.statusText);
          return null;
        }
      } catch (error) {
        console.error('Failed to fetch last review:', error);
        return null;
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap');

.feedback-title {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 20px;
  text-align: justify;
  height: 100vh;
  background: linear-gradient(to right, #e3f2fd, #f1f8e9);
  font-family: 'Roboto', sans-serif;
}

.feedback-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 85vh;
  background: linear-gradient(to right, #e3f2fd, #f1f8e9);
  font-family: 'Roboto', sans-serif;
}

.feedback-box {
  width: 70vw;
  padding: 30px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
}

h2 {
  margin-bottom: 20px;
  color: #333;
  font-weight: 700;
}

.feedback-form input[type="text"] {
  width: 100%;
  padding: 12px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 16px;
}

.star-rating {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
}

.star-rating .star {
  font-size: 30px;
  cursor: pointer;
  color: gray;
  transition: color 0.3s;
}

.star-rating .star.active,
.star-rating .star:hover {
  color: #ffcc00;
}

button {
  padding: 12px 25px;
  border: none;
  background: linear-gradient(to right, #42a5f5, #1e88e5);
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.3s;
}

button:hover {
  background: linear-gradient(to right, #1e88e5, #1565c0);
}

.error-message {
  color: red;
  margin-bottom: 15px;
}

.submitted-feedback {
  margin-top: 30px;
}

.submitted-feedback h3 {
  margin-bottom: 10px;
  color: #333;
  font-weight: 700;
}

.submitted-feedback p {
  margin-bottom: 15px;
  font-size: 16px;
}

.submitted-feedback .star-rating {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
}
</style>
